import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
  useCallback,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import type { Customer } from "@/api.types";
import { useAuth } from "./use-auth";
import { logger } from "../logger";

// Module-scope empty array so useCallback gets a stable dep reference.
const EMPTY_ROLES: readonly string[] = [];

interface SessionContextType {
  token: string | null;
  isLoading: boolean;
  isLoggedIn: boolean;
  sessionError: boolean;
  emailVerificationRequired: boolean;
  customer: Customer | null;
  roles: string[];
  hasRole: (role: string | string[]) => boolean;
  logout: () => void;
  login: () => void;
  refetchCustomer: () => void;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

interface ApiError {
  error: string;
  message?: string;
}

const fetchMe = async (token: string): Promise<Customer> => {
  const response = await axios.get<Customer>(
    `${import.meta.env.VITE_API_URL}/api/v2/customers/me`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

/**
 * Provider-agnostic session provider
 * Uses /customers/me endpoint - backend handles identity resolution
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const {
    isLoading: isAuthLoading,
    hasError: hasAuthError,
    isAuthenticated,
    getToken,
    login,
    logout: authLogout,
  } = useAuth();

  const queryClient = useQueryClient();
  const [token, setToken] = useState<string | null>(null);
  const [tokenLoading, setTokenLoading] = useState(true);

  // Get token when authenticated
  useEffect(() => {
    let active = true;
    if (isAuthLoading) {
      return;
    }

    if (!isAuthenticated) {
      // Defer to next microtask so we don't setState synchronously within the effect body.
      void Promise.resolve().then(() => {
        if (!active) return;
        setToken(null);
        setTokenLoading(false);
      });
      return () => {
        active = false;
      };
    }

    void Promise.resolve().then(() => {
      if (active) setTokenLoading(true);
    });
    getToken()
      .then((t) => {
        if (!active) return;
        setToken(t);
      })
      .catch((error) => {
        if (!active) return;
        logger.error("Failed to get token:", error);
        setToken(null);
      })
      .finally(() => {
        if (!active) return;
        setTokenLoading(false);
      });
    return () => {
      active = false;
    };
  }, [isAuthenticated, isAuthLoading, getToken]);

  // Fetch customer from /customers/me (provider-agnostic)
  const {
    data: customer,
    isLoading: isCustomerLoading,
    error: customerError,
    refetch: refetchCustomer,
  } = useQuery({
    queryKey: ["customer", "me"],
    queryFn: () => {
      if (!token) throw new Error("A session token is required.");
      return fetchMe(token);
    },
    enabled: isAuthenticated && !!token && !hasAuthError,
    retry: (failureCount, error) => {
      // Don't retry on 401/403
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        if (status === 401 || status === 403) return false;
      }
      return failureCount < 2;
    },
  });

  // Check if error is email verification required
  const emailVerificationRequired =
    !!customerError &&
    axios.isAxiosError<ApiError>(customerError) &&
    customerError.response?.data?.error === "email_verification_required";

  const logout = useCallback(() => {
    queryClient.clear();
    authLogout();
  }, [authLogout, queryClient]);

  // Roles not used in customer web (admin-only feature). `EMPTY_ROLES`
  // defined at module scope gives useCallback a stable dep reference.
  const hasRole = useCallback((requiredRoles: string | string[]) => {
    const roleArray = Array.isArray(requiredRoles)
      ? requiredRoles
      : [requiredRoles];
    return roleArray.some((role) => EMPTY_ROLES.includes(role));
  }, []);

  const isLoading =
    isAuthLoading ||
    tokenLoading ||
    (isAuthenticated &&
      !!token &&
      isCustomerLoading &&
      !emailVerificationRequired);

  const sessionError =
    hasAuthError ||
    (!isAuthLoading && !tokenLoading && isAuthenticated && !token) ||
    (!!customerError && !emailVerificationRequired);
  const isLoggedIn = isAuthenticated && !!token && !!customer && !sessionError;

  return (
    <SessionContext.Provider
      value={{
        isLoading,
        isLoggedIn,
        sessionError,
        emailVerificationRequired,
        customer: isLoggedIn ? customer : null,
        roles: EMPTY_ROLES as string[],
        token,
        hasRole,
        login,
        logout,
        refetchCustomer: () => void refetchCustomer(),
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return context;
}
